import { useState } from 'react';

interface IntakeFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

interface FamilyMember {
  living: string;
  age: string;
  diagnoses: string;
}

export default function IntakeForm({ isOpen, onClose, onSubmit }: IntakeFormProps) {
  const [formData, setFormData] = useState({
    // Patient Information
    fullName: '',
    preferredName: '',
    dateOfBirth: '',
    sexAtBirth: '',
    genderIdentity: '',
    phone: '',
    email: '',
    address: '',
    emergencyContact: '',
    primaryCareProvider: '',
    
    // Reason for Visit
    mainReason: '',
    topGoals: '',
    fhDiagnosed: '',
    geneticTesting: '',
    geneticTestingDetails: '',
    relativesWithFH: '',
    
    // Current Symptoms
    symptoms: {
      chestPain: false,
      chestPainNotes: '',
      shortnessOfBreath: false,
      shortnessOfBreathNotes: '',
      palpitations: false,
      palpitationsNotes: '',
      fainting: false,
      faintingNotes: '',
      swelling: false,
      swellingNotes: '',
      fatigue: false,
      fatigueNotes: '',
      headaches: false,
      headachesNotes: '',
      neurologicSymptoms: false,
      neurologicSymptomsNotes: '',
      calfPain: false,
      calfPainNotes: '',
      dizziness: false,
      dizzinessNotes: '',
      other: false,
      otherNotes: ''
    },
    
    // Medical Conditions
    conditions: {
      highCholesterol: false,
      fh: false,
      lipoprotein: false,
      highTriglycerides: false,
      hypertension: false,
      cad: false,
      angina: false,
      atherosclerosis: false,
      stroke: false,
      pad: false,
      valvularDisease: false,
      heartFailure: false,
      arrhythmia: false,
      diabetes: false,
      prediabetes: false,
      kidneyDisease: false,
      sleepApnea: false,
      thyroidDisease: false,
      autoimmune: false,
      otherCondition: false
    },
    conditionDetails: '',
    hospitalized: '',
    hospitalizationDetails: '',
    
    // Family History
    maternalFamily: {
      mother: { living: '', age: '', diagnoses: '' },
      maternalGrandmother: { living: '', age: '', diagnoses: '' },
      maternalGrandfather: { living: '', age: '', diagnoses: '' },
      maternalAunt1: { living: '', age: '', diagnoses: '' },
      maternalAunt2: { living: '', age: '', diagnoses: '' },
      maternalAunt3: { living: '', age: '', diagnoses: '' },
      maternalUncle1: { living: '', age: '', diagnoses: '' },
      maternalUncle2: { living: '', age: '', diagnoses: '' },
      maternalUncle3: { living: '', age: '', diagnoses: '' },
      maternalHalfSibling: { living: '', age: '', diagnoses: '' }
    },
    paternalFamily: {
      father: { living: '', age: '', diagnoses: '' },
      paternalGrandmother: { living: '', age: '', diagnoses: '' },
      paternalGrandfather: { living: '', age: '', diagnoses: '' },
      paternalAunt1: { living: '', age: '', diagnoses: '' },
      paternalAunt2: { living: '', age: '', diagnoses: '' },
      paternalAunt3: { living: '', age: '', diagnoses: '' },
      paternalUncle1: { living: '', age: '', diagnoses: '' },
      paternalUncle2: { living: '', age: '', diagnoses: '' },
      paternalUncle3: { living: '', age: '', diagnoses: '' },
      paternalHalfSibling: { living: '', age: '', diagnoses: '' }
    }
  });

  const [currentSection, setCurrentSection] = useState(1);
  const totalSections = 15;

  if (!isOpen) return null;

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSymptomChange = (symptom: string, value: boolean | string, isNote: boolean = false) => {
    setFormData(prev => ({
      ...prev,
      symptoms: {
        ...prev.symptoms,
        [symptom]: value
      }
    }));
  };

  const handleConditionChange = (condition: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [condition]: value
      }
    }));
  };

  const handleFamilyMemberChange = (side: 'maternalFamily' | 'paternalFamily', member: string, field: keyof FamilyMember, value: string) => {
    setFormData(prev => ({
      ...prev,
      [side]: {
        ...prev[side],
        [member]: {
          ...prev[side][member as keyof typeof prev[typeof side]],
          [field]: value
        }
      }
    }));
  };

  const handleNext = () => {
    if (currentSection < totalSections) {
      setCurrentSection(currentSection + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    
    // Call the onSubmit callback to open Calendly
    onSubmit();
  };

  const isLastSection = currentSection === totalSections;

  const renderFamilyMemberRow = (
    side: 'maternalFamily' | 'paternalFamily',
    memberKey: string,
    label: string
  ) => {
    const member = formData[side][memberKey as keyof typeof formData[typeof side]] as FamilyMember;
    
    return (
      <tr key={memberKey} className="border-b border-gray-200">
        <td className="py-3 px-4 text-sm text-gray-700 font-medium">{label}</td>
        <td className="py-3 px-4">
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name={`${side}-${memberKey}-living`}
                value="Yes"
                checked={member.living === 'Yes'}
                onChange={(e) => handleFamilyMemberChange(side, memberKey, 'living', e.target.value)}
                className="w-3.5 h-3.5 text-teal-500 cursor-pointer"
              />
              <span className="text-xs text-gray-700">Yes</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name={`${side}-${memberKey}-living`}
                value="No"
                checked={member.living === 'No'}
                onChange={(e) => handleFamilyMemberChange(side, memberKey, 'living', e.target.value)}
                className="w-3.5 h-3.5 text-teal-500 cursor-pointer"
              />
              <span className="text-xs text-gray-700">No</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name={`${side}-${memberKey}-living`}
                value="Unknown"
                checked={member.living === 'Unknown'}
                onChange={(e) => handleFamilyMemberChange(side, memberKey, 'living', e.target.value)}
                className="w-3.5 h-3.5 text-teal-500 cursor-pointer"
              />
              <span className="text-xs text-gray-700">Unknown</span>
            </label>
          </div>
        </td>
        <td className="py-3 px-4">
          <input
            type="text"
            value={member.age}
            onChange={(e) => handleFamilyMemberChange(side, memberKey, 'age', e.target.value)}
            placeholder="Age or age at death"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-xs"
          />
        </td>
        <td className="py-3 px-4">
          <input
            type="text"
            value={member.diagnoses}
            onChange={(e) => handleFamilyMemberChange(side, memberKey, 'diagnoses', e.target.value)}
            placeholder="Key diagnoses (include age at diagnosis)"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-xs"
          />
        </td>
      </tr>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-gray-900">Patient Intake Questionnaire</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSections }).map((_, index) => (
              <div
                key={index}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  index + 1 <= currentSection ? 'bg-teal-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-2">Section {currentSection} of {totalSections}</p>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmitForm} className="flex-1 overflow-y-auto px-8 py-6">
          {/* Section 1: Patient Information */}
          {currentSection === 1 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Patient Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Legal Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Name
                  </label>
                  <input
                    type="text"
                    value={formData.preferredName}
                    onChange={(e) => handleInputChange('preferredName', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth (MM/DD/YYYY) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sex at Birth <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.sexAtBirth}
                    onChange={(e) => handleInputChange('sexAtBirth', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="">Select...</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Intersex">Intersex</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender Identity (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.genderIdentity}
                    onChange={(e) => handleInputChange('genderIdentity', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Contact (name, relationship, phone) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.emergencyContact}
                  onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="e.g., Jane Doe, Spouse, (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Care Clinician / Cardiologist (name, clinic, phone)
                </label>
                <input
                  type="text"
                  value={formData.primaryCareProvider}
                  onChange={(e) => handleInputChange('primaryCareProvider', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="e.g., Dr. Smith, Heart Clinic, (555) 987-6543"
                />
              </div>
            </div>
          )}

          {/* Section 2: Reason for Visit and Goals */}
          {currentSection === 2 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Reason for Visit and Goals</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. What is the main reason you are seeking care today? <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={formData.mainReason}
                  onChange={(e) => handleInputChange('mainReason', e.target.value)}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.mainReason.length}/500 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. What are your top 3 goals for your heart health over the next 6 to 12 months? <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={formData.topGoals}
                  onChange={(e) => handleInputChange('topGoals', e.target.value)}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.topGoals.length}/500 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Have you ever been told you have Familial Hypercholesterolemia (FH) or a genetic cholesterol condition? <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      required
                      name="fhDiagnosed"
                      value="Yes"
                      checked={formData.fhDiagnosed === 'Yes'}
                      onChange={(e) => handleInputChange('fhDiagnosed', e.target.value)}
                      className="w-4 h-4 text-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      required
                      name="fhDiagnosed"
                      value="No"
                      checked={formData.fhDiagnosed === 'No'}
                      onChange={(e) => handleInputChange('fhDiagnosed', e.target.value)}
                      className="w-4 h-4 text-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      required
                      name="fhDiagnosed"
                      value="Unsure"
                      checked={formData.fhDiagnosed === 'Unsure'}
                      onChange={(e) => handleInputChange('fhDiagnosed', e.target.value)}
                      className="w-4 h-4 text-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Unsure</span>
                  </label>
                </div>
              </div>

              {(formData.fhDiagnosed === 'Yes' || formData.fhDiagnosed === 'Unsure') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      4. Have you had genetic testing? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          required
                          name="geneticTesting"
                          value="Yes"
                          checked={formData.geneticTesting === 'Yes'}
                          onChange={(e) => handleInputChange('geneticTesting', e.target.value)}
                          className="w-4 h-4 text-teal-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          required
                          name="geneticTesting"
                          value="No"
                          checked={formData.geneticTesting === 'No'}
                          onChange={(e) => handleInputChange('geneticTesting', e.target.value)}
                          className="w-4 h-4 text-teal-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">No</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          required
                          name="geneticTesting"
                          value="Unsure"
                          checked={formData.geneticTesting === 'Unsure'}
                          onChange={(e) => handleInputChange('geneticTesting', e.target.value)}
                          className="w-4 h-4 text-teal-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">Unsure</span>
                      </label>
                    </div>
                  </div>

                  {formData.geneticTesting === 'Yes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Which gene/result? (if known)
                      </label>
                      <input
                        type="text"
                        value={formData.geneticTestingDetails}
                        onChange={(e) => handleInputChange('geneticTestingDetails', e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. Have any close relatives been diagnosed with FH or very high LDL cholesterol? <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      required
                      name="relativesWithFH"
                      value="Yes"
                      checked={formData.relativesWithFH === 'Yes'}
                      onChange={(e) => handleInputChange('relativesWithFH', e.target.value)}
                      className="w-4 h-4 text-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      required
                      name="relativesWithFH"
                      value="No"
                      checked={formData.relativesWithFH === 'No'}
                      onChange={(e) => handleInputChange('relativesWithFH', e.target.value)}
                      className="w-4 h-4 text-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      required
                      name="relativesWithFH"
                      value="Unsure"
                      checked={formData.relativesWithFH === 'Unsure'}
                      onChange={(e) => handleInputChange('relativesWithFH', e.target.value)}
                      className="w-4 h-4 text-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Unsure</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Section 3: Current Symptoms */}
          {currentSection === 3 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Current Symptoms (Past 4 Weeks)</h3>
              <p className="text-sm text-gray-600 mb-4">Check any symptoms you have experienced and add notes (frequency, triggers, duration).</p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800">
                  <strong>Emergency Notice:</strong> If you currently have severe chest pain, trouble breathing, or signs of stroke, seek emergency care immediately.
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { key: 'chestPain', label: 'Chest pain/pressure/tightness' },
                  { key: 'shortnessOfBreath', label: 'Shortness of breath' },
                  { key: 'palpitations', label: 'Palpitations or irregular heartbeat' },
                  { key: 'fainting', label: 'Fainting/near-fainting' },
                  { key: 'swelling', label: 'Swelling in legs/ankles' },
                  { key: 'fatigue', label: 'Severe fatigue or reduced exercise tolerance' },
                  { key: 'headaches', label: 'Headaches with very high blood pressure' },
                  { key: 'neurologicSymptoms', label: 'Neurologic symptoms (weakness, facial droop, trouble speaking)' },
                  { key: 'calfPain', label: 'Calf pain with walking (possible PAD)' },
                  { key: 'dizziness', label: 'New or worsening dizziness' },
                  { key: 'other', label: 'Other (describe)' }
                ].map(symptom => (
                  <div key={symptom.key} className="border border-gray-200 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.symptoms[symptom.key as keyof typeof formData.symptoms] as boolean}
                        onChange={(e) => handleSymptomChange(symptom.key, e.target.checked)}
                        className="w-5 h-5 text-teal-500 rounded mt-0.5 cursor-pointer"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{symptom.label}</span>
                        {formData.symptoms[symptom.key as keyof typeof formData.symptoms] && (
                          <input
                            type="text"
                            value={formData.symptoms[`${symptom.key}Notes` as keyof typeof formData.symptoms] as string}
                            onChange={(e) => handleSymptomChange(`${symptom.key}Notes`, e.target.value)}
                            placeholder="Notes: frequency, triggers, duration..."
                            className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          />
                        )}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 4: Diagnosed Medical Conditions */}
          {currentSection === 4 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Diagnosed Medical Conditions</h3>
              <p className="text-sm text-gray-600 mb-4">Check all that apply</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { key: 'highCholesterol', label: 'High cholesterol / high LDL' },
                  { key: 'fh', label: 'Familial Hypercholesterolemia (FH)' },
                  { key: 'lipoprotein', label: 'Lipoprotein(a) elevated' },
                  { key: 'highTriglycerides', label: 'High triglycerides' },
                  { key: 'hypertension', label: 'Hypertension (high blood pressure)' },
                  { key: 'cad', label: 'Coronary artery disease (CAD) / prior heart attack' },
                  { key: 'angina', label: 'Angina' },
                  { key: 'atherosclerosis', label: 'Atherosclerosis / vascular disease' },
                  { key: 'stroke', label: 'Stroke / TIA' },
                  { key: 'pad', label: 'Peripheral artery disease (PAD)' },
                  { key: 'valvularDisease', label: 'Structural or valvular heart disease' },
                  { key: 'heartFailure', label: 'Heart failure' },
                  { key: 'arrhythmia', label: 'Arrhythmia (AFib, SVT, etc.)' },
                  { key: 'diabetes', label: 'Diabetes (Type 1 or Type 2)' },
                  { key: 'prediabetes', label: 'Prediabetes / insulin resistance' },
                  { key: 'kidneyDisease', label: 'Chronic kidney disease' },
                  { key: 'sleepApnea', label: 'Sleep apnea' },
                  { key: 'thyroidDisease', label: 'Thyroid disease' },
                  { key: 'autoimmune', label: 'Autoimmune disease' },
                  { key: 'otherCondition', label: 'Other (describe below)' }
                ].map(condition => (
                  <label key={condition.key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.conditions[condition.key as keyof typeof formData.conditions]}
                      onChange={(e) => handleConditionChange(condition.key, e.target.checked)}
                      className="w-4 h-4 text-teal-500 rounded cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">{condition.label}</span>
                  </label>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. For each condition checked: year diagnosed, current status, and any complications
                </label>
                <textarea
                  value={formData.conditionDetails}
                  onChange={(e) => handleInputChange('conditionDetails', e.target.value)}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="e.g., High cholesterol - diagnosed 2018, currently on statin, LDL well controlled..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.conditionDetails.length}/500 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Have you ever been hospitalized for heart or vascular issues? <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      required
                      name="hospitalized"
                      value="Yes"
                      checked={formData.hospitalized === 'Yes'}
                      onChange={(e) => handleInputChange('hospitalized', e.target.value)}
                      className="w-4 h-4 text-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      required
                      name="hospitalized"
                      value="No"
                      checked={formData.hospitalized === 'No'}
                      onChange={(e) => handleInputChange('hospitalized', e.target.value)}
                      className="w-4 h-4 text-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                </div>
                {formData.hospitalized === 'Yes' && (
                  <input
                    type="text"
                    value={formData.hospitalizationDetails}
                    onChange={(e) => handleInputChange('hospitalizationDetails', e.target.value)}
                    placeholder="When and why?"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                )}
              </div>
            </div>
          )}

          {/* Section 5: Family History */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-3">
              5) Family History (Genetic Predisposition)
            </h2>
            <p className="text-sm text-gray-600">
              Please complete as best as you can. Include ages at diagnosis and major events (heart attack, stroke, bypass/stent, sudden death). If you do not know, mark 'Unknown'.
            </p>

            {/* Maternal Side Table */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Maternal Side (your mother's family)</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Relative</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Living?</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Age / Age at death</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Key diagnoses (include age at diagnosis)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      'Mother',
                      'Maternal grandmother',
                      'Maternal grandfather',
                      'Maternal aunt 1',
                      'Maternal aunt 2',
                      'Maternal aunt 3',
                      'Maternal uncle 1',
                      'Maternal uncle 2',
                      'Maternal uncle 3',
                      'Maternal half-sibling (if any)'
                    ].map((relative) => (
                      <tr key={relative}>
                        <td className="border border-gray-300 px-4 py-2 text-sm">{relative}</td>
                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex flex-col gap-1">
                            <label className="flex items-center gap-2 text-sm">
                              <input type="radio" name={`maternal_${relative}_living`} value="yes" className="w-4 h-4" />
                              Yes
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input type="radio" name={`maternal_${relative}_living`} value="no" className="w-4 h-4" />
                              No
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input type="radio" name={`maternal_${relative}_living`} value="unknown" className="w-4 h-4" />
                              Unknown
                            </label>
                          </div>
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <input
                            type="text"
                            name={`maternal_${relative}_age`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="Age"
                          />
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <textarea
                            name={`maternal_${relative}_diagnoses`}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="Key diagnoses..."
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Paternal Side Table */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Paternal Side (your father's family)</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Relative</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Living?</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Age / Age at death</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Key diagnoses (include age at diagnosis)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      'Father',
                      'Paternal grandmother',
                      'Paternal grandfather',
                      'Paternal aunt 1',
                      'Paternal aunt 2',
                      'Paternal aunt 3',
                      'Paternal uncle 1',
                      'Paternal uncle 2',
                      'Paternal uncle 3',
                      'Paternal half-sibling (if any)'
                    ].map((relative) => (
                      <tr key={relative}>
                        <td className="border border-gray-300 px-4 py-2 text-sm">{relative}</td>
                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex flex-col gap-1">
                            <label className="flex items-center gap-2 text-sm">
                              <input type="radio" name={`paternal_${relative}_living`} value="yes" className="w-4 h-4" />
                              Yes
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input type="radio" name={`paternal_${relative}_living`} value="no" className="w-4 h-4" />
                              No
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input type="radio" name={`paternal_${relative}_living`} value="unknown" className="w-4 h-4" />
                              Unknown
                            </label>
                          </div>
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <input
                            type="text"
                            name={`paternal_${relative}_age`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="Age"
                          />
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <textarea
                            name={`paternal_${relative}_diagnoses`}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="Key diagnoses..."
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Siblings and Children */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Siblings and Children</h3>
              <p className="text-sm text-gray-600">
                List all biological siblings and children. (If adopted/step, you may list separately for social history.)
              </p>
              <textarea
                name="siblings_children"
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                placeholder="List siblings and children with relevant health information..."
              />
            </div>

            {/* Family History Questions */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Has anyone in your family had a heart attack, stent, bypass surgery, stroke, or sudden cardiac death? If yes, who and at what age?
                </label>
                <textarea
                  name="family_cardiac_events"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Describe family cardiac events..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Has anyone in your family had very high LDL cholesterol (often &gt;190 mg/dL) or been told they have FH? If yes, who?
                </label>
                <textarea
                  name="family_high_cholesterol"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Describe family cholesterol history..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Has anyone in your family had tendon xanthomas, xanthelasma, or a corneal arcus (cholesterol deposits)? If yes, who?
                </label>
                <textarea
                  name="family_cholesterol_deposits"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Describe family cholesterol deposits..."
                />
              </div>
            </div>
          </div>

          {/* Section 6: Current Medications and Supplements */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-3">
              6) Current Medications and Supplements
            </h2>
            <p className="text-sm text-gray-600">
              Include prescription medications, over-the-counter drugs, vitamins, and supplements.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Name</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Dose</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">How often</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Reason/Condition</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Start date</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <tr key={i}>
                      <td className="border border-gray-300 px-2 py-2">
                        <input
                          type="text"
                          name={`medication_${i}_name`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <input
                          type="text"
                          name={`medication_${i}_dose`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <input
                          type="text"
                          name={`medication_${i}_frequency`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <input
                          type="text"
                          name={`medication_${i}_reason`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <input
                          type="text"
                          name={`medication_${i}_start_date`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="MM/YYYY"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Have you ever stopped a medication due to side effects (for example, statin muscle pain)?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="stopped_medication" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="stopped_medication" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <textarea
                  name="stopped_medication_details"
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Describe..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Have you ever taken cholesterol-lowering medications?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="cholesterol_meds_statin" className="w-4 h-4" />
                    Statin
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="cholesterol_meds_ezetimibe" className="w-4 h-4" />
                    Ezetimibe
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="cholesterol_meds_pcsk9" className="w-4 h-4" />
                    PCSK9 inhibitor
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="cholesterol_meds_bempedoic" className="w-4 h-4" />
                    Bempedoic acid
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="cholesterol_meds_inclisiran" className="w-4 h-4" />
                    Inclisiran
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="cholesterol_meds_other" className="w-4 h-4" />
                    Other
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. If you are pregnant, breastfeeding, or trying to conceive: list any medications you were told to avoid and any alternatives recommended.
                </label>
                <textarea
                  name="pregnancy_medication_notes"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Medications to avoid and alternatives..."
                />
              </div>
            </div>
          </div>

          {/* Section 7: Allergies and Reactions */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-3">
              7) Allergies and Reactions
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Do you have any medication allergies?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="medication_allergies" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="medication_allergies" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <input
                  type="text"
                  name="medication_allergies_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="List medication and reaction"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Do you have any food allergies?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="food_allergies" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="food_allergies" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <input
                  type="text"
                  name="food_allergies_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="List food and reaction"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Do you have latex or adhesive allergy?
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="latex_allergy" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="latex_allergy" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Do you have environmental allergies (pollen, pets, etc.)?
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="environmental_allergies" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="environmental_allergies" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. Have you ever had anaphylaxis (severe allergic reaction)?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="anaphylaxis" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="anaphylaxis" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <input
                  type="text"
                  name="anaphylaxis_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Describe, include EpiPen use"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allergy details (substance, reaction, severity, date)
                </label>
                <textarea
                  name="allergy_details"
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Additional allergy details..."
                />
              </div>
            </div>
          </div>

          {/* Section 8: Surgeries, Procedures, and Hospitalizations */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-3">
              8) Surgeries, Procedures, and Hospitalizations
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. List all prior surgeries (include year and reason).
                </label>
                <textarea
                  name="prior_surgeries"
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="List surgeries..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Have you had any heart-related procedures?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="procedure_stent" className="w-4 h-4" />
                    Stent
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="procedure_bypass" className="w-4 h-4" />
                    Bypass (CABG)
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="procedure_valve" className="w-4 h-4" />
                    Valve repair/replacement
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="procedure_pacemaker" className="w-4 h-4" />
                    Pacemaker
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="procedure_icd" className="w-4 h-4" />
                    ICD
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="procedure_ablation" className="w-4 h-4" />
                    Ablation
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="procedure_other" className="w-4 h-4" />
                    Other
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Have you had LDL apheresis?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="ldl_apheresis" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="ldl_apheresis" value="no" className="w-4 h-4" />
                    No
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="ldl_apheresis" value="unsure" className="w-4 h-4" />
                    Unsure
                  </label>
                </div>
                <input
                  type="text"
                  name="ldl_apheresis_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="If yes, frequency and start year"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Any history of major injuries, ICU stays, or repeated hospital admissions?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="major_injuries" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="major_injuries" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <textarea
                  name="major_injuries_details"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Describe..."
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Procedure/Surgery</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Year</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Reason/Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <tr key={i}>
                      <td className="border border-gray-300 px-2 py-2">
                        <input
                          type="text"
                          name={`procedure_${i}_name`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <input
                          type="text"
                          name={`procedure_${i}_year`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="YYYY"
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-2">
                        <input
                          type="text"
                          name={`procedure_${i}_reason`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 9: Implants, Medical Devices, and Physical Disabilities */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-3">
              9) Implants, Medical Devices, and Physical Disabilities
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Do you have any implanted devices?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="implant_pacemaker" className="w-4 h-4" />
                    Pacemaker
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="implant_icd" className="w-4 h-4" />
                    ICD
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="implant_loop_recorder" className="w-4 h-4" />
                    Loop recorder
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="implant_stent" className="w-4 h-4" />
                    Stent(s)
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="implant_valve" className="w-4 h-4" />
                    Artificial valve
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="implant_joint" className="w-4 h-4" />
                    Joint replacement
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="implant_other" className="w-4 h-4" />
                    Other
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Do you have any non-cardiac implants? (for example, cochlear implant, shunt, metal hardware)
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="non_cardiac_implants" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="non_cardiac_implants" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <input
                  type="text"
                  name="non_cardiac_implants_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Describe..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Do you have any physical disabilities or mobility limitations?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="physical_disabilities" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="physical_disabilities" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <textarea
                  name="physical_disabilities_details"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Describe..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Do you use assistive devices?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="assistive_cane" className="w-4 h-4" />
                    Cane
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="assistive_walker" className="w-4 h-4" />
                    Walker
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="assistive_wheelchair" className="w-4 h-4" />
                    Wheelchair
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="assistive_prosthesis" className="w-4 h-4" />
                    Prosthesis
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="assistive_other" className="w-4 h-4" />
                    Other
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. Do you have chronic pain conditions that affect activity?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="chronic_pain" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="chronic_pain" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <textarea
                  name="chronic_pain_details"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Describe..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Implant/device details (type, date placed, manufacturer/model if known)
                </label>
                <textarea
                  name="implant_device_details"
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Additional implant/device details..."
                />
              </div>
            </div>
          </div>

          {/* Section 10: Mental Health History */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-3">
              10) Mental Health History
            </h2>
            <p className="text-sm text-gray-600">
              Mental health can affect cardiovascular risk through sleep, stress hormones, medication effects, and lifestyle. Your answers help us support you safely.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Have you ever been diagnosed with a mental health condition?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="mental_health_depression" className="w-4 h-4" />
                    Depression
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="mental_health_anxiety" className="w-4 h-4" />
                    Anxiety
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="mental_health_bipolar" className="w-4 h-4" />
                    Bipolar disorder
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="mental_health_ptsd" className="w-4 h-4" />
                    PTSD
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="mental_health_adhd" className="w-4 h-4" />
                    ADHD
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="mental_health_eating" className="w-4 h-4" />
                    Eating disorder
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="mental_health_other" className="w-4 h-4" />
                    Other
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Are you currently receiving therapy or counseling?
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="therapy" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="therapy" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Have you ever been hospitalized for mental health reasons?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="mental_health_hospitalized" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="mental_health_hospitalized" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <input
                  type="text"
                  name="mental_health_hospitalized_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="When and why"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Do you have a history of self-harm or suicidal thoughts?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="self_harm" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="self_harm" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <input
                  type="text"
                  name="self_harm_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="If yes, are you safe today?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. Do you currently take any medications for mental health (antidepressants, stimulants, etc.)?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="mental_health_medications" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="mental_health_medications" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <input
                  type="text"
                  name="mental_health_medications_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="List in medication section"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional mental health notes (triggers, supports, safety plan if applicable)
                </label>
                <textarea
                  name="mental_health_notes"
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          </div>

          {/* Section 11: Substance Use History */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-3">
              11) Substance Use History
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Tobacco/nicotine:
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="tobacco" value="never" className="w-4 h-4" />
                    Never
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="tobacco" value="former" className="w-4 h-4" />
                    Former
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="tobacco" value="current" className="w-4 h-4" />
                    Current
                  </label>
                </div>
                <input
                  type="text"
                  name="tobacco_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Cigarettes, vape, chewing - amount per day and years used"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Alcohol:
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="alcohol" value="none" className="w-4 h-4" />
                    None
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="alcohol" value="occasional" className="w-4 h-4" />
                    Occasional
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="alcohol" value="weekly" className="w-4 h-4" />
                    Weekly
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="alcohol" value="daily" className="w-4 h-4" />
                    Daily
                  </label>
                </div>
                <input
                  type="text"
                  name="alcohol_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Typical drinks per week and binge episodes"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Cannabis:
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="cannabis" value="never" className="w-4 h-4" />
                    Never
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="cannabis" value="former" className="w-4 h-4" />
                    Former
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="cannabis" value="current" className="w-4 h-4" />
                    Current
                  </label>
                </div>
                <input
                  type="text"
                  name="cannabis_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Type (smoke/edible), frequency"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Other substances (cocaine, meth, opioids not prescribed, etc.):
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="other_substances" value="never" className="w-4 h-4" />
                    Never
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="other_substances" value="former" className="w-4 h-4" />
                    Former
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="other_substances" value="current" className="w-4 h-4" />
                    Current
                  </label>
                </div>
                <input
                  type="text"
                  name="other_substances_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Substance, frequency, last use"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. Have you ever been treated for substance use disorder?
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="substance_treatment" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="substance_treatment" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
                <input
                  type="text"
                  name="substance_treatment_details"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  placeholder="Program, dates"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  6. Have you ever had withdrawal seizures, delirium tremens, or overdose?
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="withdrawal_complications" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="withdrawal_complications" value="no" className="w-4 h-4" />
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Section 12: Reproductive and Hormonal Health */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-3">
              12) Reproductive and Hormonal Health (if applicable)
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Are you currently pregnant?
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="currently_pregnant" value="yes" className="w-4 h-4" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="currently_pregnant" value="no" className="w-4 h-4" />
                    No
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="currently_pregnant" value="not_applicable" className="w-4 h-4" />
                    Not applicable
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. If pregnant: current week of pregnancy _______; any complications (e.g., preeclampsia, gestational diabetes, hypertension)?
                </label>
                <textarea
                  name="reproductive_pregnancy_complications"
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Week of pregnancy and any complications"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Have you ever had pregnancy-related high blood pressure (gestational hypertension, preeclampsia)?
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="reproductive_pregnancy_hypertension" value="Yes" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="reproductive_pregnancy_hypertension" value="No" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                  <input
                    type="text"
                    name="reproductive_pregnancy_hypertension_details"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Which pregnancy, outcomes"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Are you breastfeeding?
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="reproductive_breastfeeding" value="Yes" className="w-4 h-4 text-teal-600" />
                    <span className="text-sm">Yes</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="reproductive_breastfeeding" value="No" className="w-4 h-4 text-teal-600" />
                    <span className="text-sm">No</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. Are you trying to conceive in the next 12 months?
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="reproductive_trying_conceive" value="Yes" className="w-4 h-4 text-teal-600" />
                    <span className="text-sm">Yes</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="reproductive_trying_conceive" value="No" className="w-4 h-4 text-teal-600" />
                    <span className="text-sm">No</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  6. Menstrual status:
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="reproductive_menstrual_status" value="Regular" className="w-4 h-4 text-teal-600 rounded" />
                    <span className="text-sm">Regular</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="reproductive_menstrual_status" value="Irregular" className="w-4 h-4 text-teal-600 rounded" />
                    <span className="text-sm">Irregular</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="reproductive_menstrual_status" value="Postpartum" className="w-4 h-4 text-teal-600 rounded" />
                    <span className="text-sm">Postpartum</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="reproductive_menstrual_status" value="Perimenopause" className="w-4 h-4 text-teal-600 rounded" />
                    <span className="text-sm">Perimenopause</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="reproductive_menstrual_status" value="Menopause" className="w-4 h-4 text-teal-600 rounded" />
                    <span className="text-sm">Menopause</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="reproductive_menstrual_status" value="Hysterectomy" className="w-4 h-4 text-teal-600 rounded" />
                    <span className="text-sm">Hysterectomy</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  7. Age at menopause (if applicable):
                </label>
                <input
                  type="text"
                  name="reproductive_menopause_age"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Age at menopause"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  8. Do you use hormonal contraception or hormone therapy?
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="reproductive_hormone_therapy" value="Yes" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="reproductive_hormone_therapy" value="No" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                  <input
                    type="text"
                    name="reproductive_hormone_therapy_details"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Type, duration"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 13: Lifestyle */}
          <section className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">13) Lifestyle (Diet, Activity, Sleep, Stress)</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Typical weekly physical activity:
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Minutes of moderate activity</label>
                    <input
                      type="text"
                      name="lifestyle_moderate_activity"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Minutes per week"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Vigorous activity</label>
                    <input
                      type="text"
                      name="lifestyle_vigorous_activity"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Minutes per week"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Strength training days</label>
                    <input
                      type="text"
                      name="lifestyle_strength_training"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Days per week"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Do you experience chest pain, shortness of breath, or dizziness with exercise?
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="lifestyle_exercise_symptoms" value="Yes" className="w-4 h-4 text-teal-600" />
                    <span className="text-sm">Yes</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="lifestyle_exercise_symptoms" value="No" className="w-4 h-4 text-teal-600" />
                    <span className="text-sm">No</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Describe your usual diet pattern (e.g., Mediterranean, vegetarian, low-carb, high-protein, traditional/cultural diet).
                </label>
                <textarea
                  name="lifestyle_diet_pattern"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Describe your typical diet pattern"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. How many days per week do you eat fast food or highly processed foods?
                </label>
                <input
                  type="text"
                  name="lifestyle_fast_food_days"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Days per week"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. How many servings per day of fruits/vegetables?
                </label>
                <input
                  type="text"
                  name="lifestyle_fruits_vegetables"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Servings per day"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  6. Sleep: average hours per night _______; do you snore loudly or stop breathing during sleep (reported)?
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="lifestyle_sleep_hours"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Average hours per night"
                  />
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">Snore loudly or stop breathing:</span>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="lifestyle_sleep_apnea" value="Yes" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="lifestyle_sleep_apnea" value="No" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  7. Stress level (0-10): _______; main stressors and supports
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="lifestyle_stress_level"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Stress level (0-10)"
                  />
                  <textarea
                    name="lifestyle_stressors_supports"
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Main stressors and supports"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  8. Do you have access to safe places to exercise and healthy food?
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="lifestyle_access_resources" value="Yes" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="lifestyle_access_resources" value="No" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                  <textarea
                    name="lifestyle_access_barriers"
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Describe barriers if any"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Section 14: Recent Measurements and Tests */}
          <section className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">14) Recent Measurements and Tests</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Most recent blood pressure reading (if known):
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    name="tests_bp_systolic"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Systolic"
                  />
                  <input
                    type="text"
                    name="tests_bp_diastolic"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Diastolic"
                  />
                  <input
                    type="text"
                    name="tests_bp_date"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Date"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Most recent cholesterol panel (if known):
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <input
                    type="text"
                    name="tests_cholesterol_total"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Total"
                  />
                  <input
                    type="text"
                    name="tests_cholesterol_ldl"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="LDL"
                  />
                  <input
                    type="text"
                    name="tests_cholesterol_hdl"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="HDL"
                  />
                  <input
                    type="text"
                    name="tests_cholesterol_tg"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="TG"
                  />
                  <input
                    type="text"
                    name="tests_cholesterol_date"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Date"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Have you ever had advanced lipid testing (ApoB, Lp(a))?
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tests_advanced_lipid" value="Yes" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tests_advanced_lipid" value="No" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">No</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tests_advanced_lipid" value="Unsure" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">Unsure</span>
                    </label>
                  </div>
                  <input
                    type="text"
                    name="tests_advanced_lipid_details"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Results/date"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Have you ever had coronary calcium score (CAC), CT angiography, stress test, echocardiogram, or carotid ultrasound?
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tests_cardiac_imaging" value="Yes" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tests_cardiac_imaging" value="No" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                  <textarea
                    name="tests_cardiac_imaging_details"
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Results/date"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. Diabetes labs:
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Last A1c</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="tests_a1c"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="A1c value"
                      />
                      <input
                        type="text"
                        name="tests_a1c_date"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Date"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Fasting glucose</label>
                    <input
                      type="text"
                      name="tests_fasting_glucose"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Fasting glucose"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  6. Kidney labs:
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Last creatinine/eGFR</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="tests_kidney_labs"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Creatinine/eGFR"
                      />
                      <input
                        type="text"
                        name="tests_kidney_labs_date"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Date"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Urine protein test?</label>
                    <div className="flex items-center gap-4 h-full">
                      <div className="flex items-center gap-2">
                        <input type="radio" name="tests_urine_protein" value="Yes" className="w-4 h-4 text-teal-600" />
                        <span className="text-sm">Yes</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="radio" name="tests_urine_protein" value="No" className="w-4 h-4 text-teal-600" />
                        <span className="text-sm">No</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  7. Genetic testing:
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input type="radio" name="tests_genetic_testing" value="None" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">None</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="radio" name="tests_genetic_testing" value="Ordered" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">Ordered</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="radio" name="tests_genetic_testing" value="Completed" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm">Completed</span>
                    </div>
                  </div>
                  <input
                    type="text"
                    name="tests_genetic_testing_details"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Lab/date/results if known"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attach or upload: prior lab reports, imaging, genetic test report, discharge summaries, medication list.
                </label>
                <textarea
                  name="tests_attachments_notes"
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="List any documents you will provide or upload"
                />
              </div>
            </div>
          </section>

          {/* Section 15: Care Preferences and Safety */}
          <section className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">15) Care Preferences and Safety</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Preferred language: __________________ Need interpreter?
                </label>
                <div className="flex gap-4">
                  <input
                    type="text"
                    name="care_preferred_language"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Preferred language"
                  />
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input type="radio" name="care_need_interpreter" value="Yes" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm whitespace-nowrap">Yes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="radio" name="care_need_interpreter" value="No" className="w-4 h-4 text-teal-600" />
                      <span className="text-sm whitespace-nowrap">No</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Any cultural, religious, or personal preferences that should guide your care?
                </label>
                <textarea
                  name="care_cultural_preferences"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Describe any preferences"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Do you consent to your clinician requesting records from other providers?
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input type="radio" name="care_consent_records" value="Yes" className="w-4 h-4 text-teal-600" />
                    <span className="text-sm">Yes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" name="care_consent_records" value="No" className="w-4 h-4 text-teal-600" />
                    <span className="text-sm">No</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Who should have access to your health information? (spouse, caregiver, etc.)
                </label>
                <textarea
                  name="care_health_info_access"
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="List authorized individuals"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. Anything else you want your care team to know?
                </label>
                <textarea
                  name="care_additional_notes"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Any additional information"
                />
              </div>

              <div className="pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signature (patient/guardian) and date
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="care_signature"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Full name (signature)"
                    required
                  />
                  <input
                    type="date"
                    name="care_signature_date"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
              </div>
            </div>
          </section>
        </form>

        {/* Footer Navigation */}
        <div className="px-8 py-6 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            onClick={currentSection === 1 ? onClose : handlePrevious}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            {currentSection === 1 ? 'Cancel' : 'Previous'}
          </button>

          <div className="flex items-center gap-3">
            {!isLastSection ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Next Section
              </button>
            ) : (
              <button
                type="submit"
                onClick={handleSubmitForm}
                className="px-6 py-2.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Submit & Schedule
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}